import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { loadDocument, listRecentDocuments, type RecentDocumentsPage } from '@data/services/documentReader';
import { loadBusinessIdentityWithImages } from '@data/services/businessReader';
import type { BusinessIdentity } from '@core/doc/renderA4';
import { quotationFormOptions, createQuotationDraft, updateQuotationDraft, type QuotationInput } from '@data/services/quotationService';
import { currentBusinessId, DOCUMENTS_QUERY_ROOT } from '@features/documents/useDocuments';

export const QUOTATIONS_QUERY_ROOT = ['quotations'] as const;

export function useQuotationOptions() {
  return useQuery({
    queryKey: [...QUOTATIONS_QUERY_ROOT, 'options'],
    queryFn: async () => {
      const id = await currentBusinessId();
      return id ? quotationFormOptions(getDb() as never, id) : { customers: [], products: [] };
    },
  });
}

export function useQuotations() {
  return useQuery({
    queryKey: [...QUOTATIONS_QUERY_ROOT, 'list'],
    queryFn: async (): Promise<RecentDocumentsPage> => {
      const id = await currentBusinessId();
      if (!id) return { documents: [], total: 0, truncated: false, partyNamesByDocumentId: new Map() };
      return listRecentDocuments(getDb() as never, id, 200, true, 'QUOTATION') as Promise<RecentDocumentsPage>;
    },
  });
}

export function useQuotation(documentId: string | null) {
  return useQuery({
    queryKey: [...QUOTATIONS_QUERY_ROOT, 'document', documentId],
    enabled: documentId !== null,
    queryFn: async () => {
      if (!documentId) return null;
      const business = await resolveActiveBusiness(getDb() as never);
      const stored = await getDb().documents.get(documentId);
      if (stored?.docType !== 'QUOTATION') throw new Error('The requested document is not a quotation.');
      const [model, businessIdentity, conversions] = await Promise.all([
        loadDocument(getDb() as never, documentId, { businessStateCode: business.stateCode, businessId: business.id }),
        loadBusinessIdentityWithImages(getDb() as never, business.id),
        getDb().documentLinks.where('toDocumentId').equals(documentId).toArray(),
      ]);
      const salesOrderId = conversions.find((link) => link.relation === 'CONVERTED_FROM' && link.fromDocType === 'SALES_ORDER')?.fromDocumentId;
      return { model, stored, salesOrderId, business: businessIdentity as BusinessIdentity };
    },
  });
}

export function useCreateQuotation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: QuotationInput) => createQuotationDraft(getDb() as never, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT });
      await client.invalidateQueries({ queryKey: QUOTATIONS_QUERY_ROOT });
    },
  });
}

export function useUpdateQuotation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Omit<QuotationInput, 'businessId'> }) => updateQuotationDraft(getDb() as never, id, input),
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT });
      await client.invalidateQueries({ queryKey: QUOTATIONS_QUERY_ROOT });
      await client.invalidateQueries({ queryKey: [...QUOTATIONS_QUERY_ROOT, 'document', result.id] });
    },
  });
}
